/**
 * Real-state renderers — the designed non-data states (design README §6).
 * Every state names itself in plain words a Level-1 ops user can read aloud:
 * loading, endpoint error (named failure), unreachable deployment, empty.
 * Nothing here can ever render fake success (ux-principles §3).
 */

import type { ReactNode } from 'react'

export function LoadingState({ label }: { label: string }) {
  return (
    <div role="status" aria-live="polite" className="state state-loading" data-testid="state-loading">
      <p>{label}</p>
    </div>
  )
}

/**
 * Endpoint error — names the failure verbatim (the store carries the BFF/
 * network message) and offers retry; no data is presented as current.
 */
export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div role="alert" className="state state-error" data-testid="state-error">
      <p>{message}</p>
      {onRetry !== undefined && (
        <button type="button" onClick={onRetry} className="button-retry" data-testid="state-error-retry">
          Retry
        </button>
      )}
    </div>
  )
}

/**
 * Empty — "No invoices in period" with the scope restated (customer /
 * TPIN-branch / period). Never a blank region or a bare zero reading as data.
 */
export function EmptyState({ scope }: { scope: string }) {
  return (
    <div role="status" className="state state-empty" data-testid="state-empty">
      <p>No invoices in period.</p>
      <p className="state-empty-scope">{scope}</p>
    </div>
  )
}

/**
 * Deployment unreachable — explicit per-deployment state with the named
 * reason (PRD behaviour detail; ADR-000 §1.2). Never an empty row that could
 * read as "zero activity".
 */
export function UnreachableState({ dbName, reason }: { dbName: string; reason: string | null }) {
  return (
    <div role="alert" className="state state-unreachable" data-testid="state-unreachable">
      <p>
        Unreachable: {dbName}
        {reason !== null && reason !== '' ? ` — ${reason}` : ''}
      </p>
    </div>
  )
}

/**
 * Wrapper marking previously retrieved data during refresh (FR-6: data is
 * never presented as current while unverified).
 */
export function RefreshingFrame({ children, refreshing }: { children: ReactNode; refreshing: boolean }) {
  return (
    <div data-refreshing={refreshing ? 'true' : undefined} aria-busy={refreshing || undefined}>
      {refreshing && (
        <div role="status" aria-live="polite" className="refresh-banner" data-testid="refresh-banner">
          <p>Refreshing — showing previously retrieved data until the fresh result arrives.</p>
        </div>
      )}
      {children}
    </div>
  )
}
